/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_form_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_save
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_error_create_failed
import studio.oddpods.cauce.feature.patients.generated.resources.patients_skip_action

@Composable
fun AddPatientWithSessionScreen(
    mode: AddPatientMode,
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    onNavigateToDashboard: () -> Unit = {},
    viewModel: AddPatientWithSessionViewModel = koinViewModel { parametersOf(mode) },
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val sessionErrorMessage = stringResource(Res.string.patients_session_error_create_failed)

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            AddPatientWithSessionEvent.NavigateBack -> onBackClick()
            AddPatientWithSessionEvent.NavigateToDashboard -> onNavigateToDashboard()
            AddPatientWithSessionEvent.ShowSessionError -> {
                snackbarHostState.showSnackbar(sessionErrorMessage)
            }
        }
    }

    KptScaffold(
        showNavigationIcon = mode is AddPatientMode.Regular,
        onNavigationIconClick = onBackClick,
        title = stringResource(Res.string.patients_form_title),
        snackbarHost = { androidx.compose.material3.SnackbarHost(snackbarHostState) },
        modifier = modifier,
    ) {
        AddPatientWithSessionContent(
            state = state,
            dateFormatter = viewModel.dateFormatter,
            onAction = { viewModel.trySendAction(it) },
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp),
        ) {
            Button(
                onClick = { viewModel.trySendAction(AddPatientWithSessionAction.SaveClicked) },
                enabled = !state.isSaving,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 8.dp),
            ) {
                Text(stringResource(Res.string.patients_save))
            }

            if (mode is AddPatientMode.Onboarding) {
                TextButton(
                    onClick = { viewModel.trySendAction(AddPatientWithSessionAction.SkipClicked) },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(stringResource(Res.string.patients_skip_action))
                }
            }
        }
    }
}
